import NavigationBar from "../comps/NavigationBar/NavigationBar";
import Footer from "../comps/Footer/Footer";
import ContentCard from "../comps/ContentCard/ContentCard";
import Sidebar from "../comps/Sidebar/Sidebar";

export default function Engineering() {
  const menu = [
    {
      name: "1.Forward Engineering - eCIS Prediction",
      id: "Engineering-0",
    },
    {
      name: "2.Reverse Engineering - Discovering the Programmability of eCISs",
      id: "Engineering-1",
    },
    {
      name: "Future",
      id: "Engineering-2",
    },
  ];
  return (
    <div className="general-background">
      <NavigationBar />
      <Sidebar menu={menu} />
      <div className="main-container">
        <p className="title">Engineering:</p>
        <ContentCard
          content="
              <p>
              This year, our goal is to predict and understand eCISs. To achieve this, BioRocket uses a combination of forward engineering and reverse engineering strategies, both of which follow the engineering design cycle: Problem Identification → Design → Construction → Testing → Learning → Redesign...
              </p>
              <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure1.png'
              alt='Figure 1. The engineering framework of BioRocket'
              class='content-figure'
              />
              <p class='content-legend'>
              Figure 1. The engineering framework of BioRocket
              </p>
              "
        />
        <p className="subtitle" key={"Engineering-0"} id="Engineering-0">
          1. Forward Engineering - eCIS Prediction
        </p>
        <ContentCard
          content={`
            <b>1.1 Problem Identification:</b>
            <p>
            eCISs present in prokaryotic genomes, typically formed by an operon of 15–28 genes. To successfully mine eCISs, the key lies in predicting the essential protein-coding genes within the gene clusters.
            </p>
            <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure2.png'
              alt='Figure 2. Genetic composition of three known eCISs'
              class='content-figure'
              />
            <p class='content-legend'>
            Figure 2. Genetic composition of three known eCISs
            </p>
            <br />
            <b>1.2 Design:</b>
            <p>
            Through the examination of three experimentally confirmed eCISs (AFP, PVC, and MAC), we have identified eleven protein types that exhibit conservation within these eCIS loci. Furthermore, in the context of P. luteoviolacea, the inactivation of individual genes macS, macT1/2, or macB (which correspond to afp2/3/4, afp1/5, and afp11 homologous) resulted in the disappearance of the MAC phenotype[1]. This underscores the fundamental significance of these genes for their functional integrity.
            </p>
            <br />
            <p>
            The challenge in predicting eCISs lies in avoiding the misidentification of phages and other contractile injection systems as eCISs. By contrasting the distinct composition of eCISs with that of phages and other CIS systems and preliminary experiments (more on <a href="https://2023.igem.wiki/sysu-software/model">Model</a>), we have identified afp13 as a pivotal and distinctive protein within eCISs.
            </p>
            <br />
            <p>
            Furthermore, we consulted Professor Jiang Feng, who was the first to elucidate the crystal structure of PVC (one type of eCISs). Drawing from his insights into the structure and functionality of eCISs, he deems the inclusion of afp13 in the search criteria to be an appropriate choice.
            </p>
            <br />
            <p>
            In the light of the information above, we have established the simultaneous presence of afp1/5, afp11, and afp13 as our search criteria for the initial large-scale screening of genomes to identify potential eCISs.
            </p>
            <br />
            <b>1.3 Build:</b>
            <p>
            Our initial building plan consists of the following stages:
            </p>
            <br />
            <p>
            1. Data Gathering: Download eCIS data from existing databases and check the reliability of the data.
            </p>
            <br />
            <p>
            2. Genome Mining: Utilize the GTDB database to encompass the majority of known microbial groups. Simultaneously considering sequence and structural information.
            </p>
            <br />
            <p>
            3. Testing and Validation: Ensure the reliability of the mined data through validation sets and wet experiments.
            </p>
            <br />
            <p>
            4. Learn: Adjusting the model parameters to enhance prediction accuracy.
            </p>
            <br />
            <b>1.4 Test and Validation:</b>
            <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure3.png'
              alt='Figure 3. Three validation methods involved in BioRocket'
              class='content-figure'
              />
            <p class='content-legend'>
            Figure 3. Three validation methods involved in BioRocket
            </p>
            <br />
            <b>1.5 Learn:</b>
            <p>
            At the outset, we primarily focused on mining eCIS complexes from the perspective of sequence information. However, we later found that this approach was not yielding the best results. This is because the relationship between a protein's one-dimensional sequence information and its function is highly complex, and existing artificial intelligence models still struggle to fully fit the relatively limited training samples. Therefore, in our subsequent efforts, we incorporated structural information into the mining process through large-scale structural simulations. This significantly enhanced the accuracy of BioRocket.
            </p>
        `}
        />
        <p className="subtitle" key={"Engineering-1"} id="Engineering-1">
          2. Reverse Engineering - Discovering the Programmability of eCISs
        </p>
        <ContentCard
          content={`
            <b>2.1 Problem Identification:</b>
            <p>
            The potential advantage of the eCISs as a delivery system lies in its programmable targeting capabilities, akin to bacteriophage systems. Existing research[2] has indicated that within the eCIS complex, the afp13 protein contains a receptor recognition region that can be engineered for the customization of eCIS targeting. However, due to data limitations, previous studies have been confined to a single eCIS source originating from the Photorhabdus genus. By leveraging diverse eCIS data through BioRocket, we can explore whether programmability is universally present and provide a more accurate description of its characteristics and patterns.
            </p>
            <br />
            <b>2.2 Design:</b>
            <p>
            In nature, there are certain programmable systems, such as CRISPR, which share a common characteristic: they exhibit constant sequence preservation, but within a very short and defined window, they display an enormous amount of sequence diversity. This diversification serves as an indicator of how nature evolves programmability over time.
            </p>
            <br />
            <p>
            Due to the limited availability of eCIS data in previous research, comparisons were only possible between eCISs from the Photorhabdus genus and homologous sequences in phages. Leveraging data generated by BioRocket, we performed multiple sequence alignments of afp13 proteins from eCISs. By calculating the amino acid sequence conservation at different positions, we identified highly variable regions within these proteins, as well as extremely conserved sites at the ends of these regions. This approach allowed us to annotate the receptor recognition regions of different eCISs in a high-throughput manner.
            </p>
            <br />
            <b>2.3 Build:</b>
            <p>
            We first conducted a multiple sequence alignment using MAFFT between the PVC's afp13 homologous protein and 30 afp13 homologous proteins identified by BioRocket.
            </p>
            <br />
            <p>
            We calculated the site conservation score in the multiple sequence alignment file corresponding to the receptor-binding domain of the PVC sequence's tail fiber protein. We identified highly conserved sites at the boundary region of the receptor-binding domain. This suggests that this programmable region, which determines specificity, may be widely present in eCIS afp13.
            </p>
            <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure4.png'
              alt='Figure 4. Identify the programmable region of afp13 homologous protein through the recognition of highly conserved sites.'
              class='content-figure'
              />
            <p class='content-legend'>
            Figure 4. Identify the programmable region of afp13 homologous protein through the recognition of highly conserved sites.
            </p>
            <p>
            Moreover, upon determining the boundary sites, similar computational methods can be employed to identify the programmable regions in other afp13 homologous proteins.
            </p>
            <br />
            <b>2.4 Test and Validation:</b>
            <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure5.jpeg'
              alt='Figure 5. Predicted structure of afp13 homologous protein from Nitrococcus mobilis Nb-231'
              class='content-figure'
              />
            <p class='content-legend'>
            Figure 5. Predicted structure of afp13 homologous protein from Nitrococcus mobilis Nb-231
            </p>
            <br />
            <p>
            To validate the accuracy of BioRocket's method for identifying programmable regions, we selected an eCIS from Nitrococcus mobilis Nb-231 (discovered by BioRocket mining process). Using the aforementioned approach, we identified the receptor-binding region of the afp13 homologous protein within it. Through alphafold structural modeling (submitted in trimeric form), we observed the intrinsic structural region at the N-terminus, the programmable region in the middle, and the C-terminal domain on the afp13 homologous protein. When the programmable region was deleted, the N-terminal intrinsic structural region could still form a trimeric structure.
            </p>
            <br />
            <b>2.5 Learn:</b>
            <p>
            The prediction of the programmable region of the eCISs by BioRocket provides valuable guidance for researchers to gain a deeper understanding of its targeting capabilities. However, eCIS is a complex protein complex with both intricate structure and function, and its targeting specificity is influenced by numerous factors. BioRocket will continue to explore this direction in the future by combining algorithms with wet lab experiments.
            </p>
        `}
        />
        <p className="subtitle" key={"Engineering-2"} id="Engineering-2">
          3. Future
        </p>
        <ContentCard
          content={`
            <p>
            We believe that in the future, the interaction of synthetic biology, laboratory automation, and artificial intelligence will enable a higher degree of engineering for biological systems. 
            </p>
            <div style='text-align: center'>  
            <img
              src='https://static.igem.wiki/teams/4826/wiki/engineering-figure6.png'
              alt='Figure 6. New paradigm in synthetic biology research'
              class='content-figure'
              style='width:50%'
            />
            </div>  
            <p class='content-legend'>
            Figure 6. New paradigm in synthetic biology research
            </p>
            <br />
            <p>
            In our project design, we aimed to achieve an iterative exploration of the eCISs under the guidance of AI. However, due to limitations in wet lab experiment throughput, achieving a complete closed loop wasn't feasible. Therefore, after the conclusion of this year's iGEM, we plan to continue refining our project through commercial collaborations with Wet lab platform companies to further optimize our approach (more on <a href="https://2023.igem.wiki/sysu-software/entrepreneurship">Entrepreneurship</a>).
            </p>
          `}
        />
        <p className="reference-title">• Reference:</p>
        <ContentCard
          content="
          <p class='reference'>
          [1] Shikuma NJ, et al. Marine tubeworm metamorphosis induced by arrays of bacterial phage tail-like structures. Science. 2014;343:529-533. doi:10.1126/science.1246794.
          </p>
          <p class='reference'>
          [2]Kreitz J, Friedrich MJ, Guru A, et al. Programmable protein delivery with a bacterial contractile injection system. Nature. 2023;616(7956):357-364. doi:10.1038/s41586-023-05870-7 
          </p>
        "
        />
      </div>
      <Footer />
    </div>
  );
}
